// Sign-up Activation Funnel SQL. Spine is public.user_roles (status='active');
// one row per (user, company) pair. Activity aggregates attribute to the
// company in which it actually happened. Cohort window is open all-time;
// filtering is applied client-side.

import { logrocketLink, mixpanelLink } from "../../lib/funnel/helpers";

export const SIGNUP_FUNNEL_SQL = `
WITH cohort AS (
    SELECT '2020-01-01'::timestamptz AS start_ts,
           '2099-01-01'::timestamptz AS end_ts
),

transcripts AS (
    SELECT
        t.user_external_id,
        t.company_external_id,
        COUNT(*)                          AS transcript_count,
        MIN(t.created_date_utc)           AS first_transcript_date,
        MAX(t.created_date_utc)           AS last_transcript_date
    FROM public.meeting_transcription t
    JOIN public.meeting m ON m.external_id = t.meeting_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
      AND t.deleted_date_utc IS NULL
    GROUP BY t.user_external_id, t.company_external_id
),

synopses AS (
    SELECT
        s.user_external_id,
        s.company_external_id,
        COUNT(*)                          AS synopsis_count,
        MIN(s.created_date_utc)           AS first_synopsis_date,
        MAX(s.created_date_utc)           AS last_synopsis_date
    FROM public.meeting_synopsis s
    JOIN public.meeting m ON m.external_id = s.meeting_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
    GROUP BY s.user_external_id, s.company_external_id
),

chats AS (
    SELECT
        c.user_external_id,
        c.company_external_id,
        COUNT(*) FILTER (
            WHERE c.meeting_external_id IS NOT NULL
               OR c.synopsis_external_id IS NOT NULL
        )                                  AS chat_meeting_count,
        COUNT(*) FILTER (
            WHERE c.client_external_id IS NOT NULL
        )                                  AS chat_client_count,
        MIN(c.created_date_utc) FILTER (
            WHERE c.meeting_external_id IS NOT NULL
               OR c.synopsis_external_id IS NOT NULL
        )                                  AS first_chat_meeting_date,
        MIN(c.created_date_utc) FILTER (
            WHERE c.client_external_id IS NOT NULL
        )                                  AS first_chat_client_date
    FROM public.chat c
    WHERE c.deleted_date_utc IS NULL
    GROUP BY c.user_external_id, c.company_external_id
),

custom_templates AS (
    SELECT
        t.user_external_id,
        t.company_external_id,
        COUNT(*)                          AS custom_template_count,
        MIN(t.created_date_utc)           AS first_custom_template_date
    FROM public.template t
    JOIN public.users tu ON tu.external_id = t.user_external_id
    WHERE t.created_date_utc > tu.created_at_utc + interval '5 minutes'
      AND t.is_archived = false
    GROUP BY t.user_external_id, t.company_external_id
),

calendar_first AS (
    SELECT
        cal.user_external_id,
        cal.company_external_id,
        MIN(cal.created_date_utc) AS first_calendar_date
    FROM public.calendar cal
    GROUP BY cal.user_external_id, cal.company_external_id
),

company_size AS (
    SELECT
        ur.company_external_id,
        COUNT(DISTINCT ur.user_external_id) AS marloo_active_user_count
    FROM public.user_roles ur
    WHERE ur.status = 'active'
    GROUP BY ur.company_external_id
),

billing AS (
    SELECT
        cb.company_external_id,
        bool_or(cb.billing_plan::text IN ('plus','pro')) AS on_paid_plan,
        MIN(cb.subscription_started_date_utc) FILTER (
            WHERE cb.billing_plan::text IN ('plus','pro')
        )                                            AS subscription_started_date_utc,
        (array_agg(cb.billing_plan::text ORDER BY cb.subscription_started_date_utc DESC NULLS LAST))[1] AS billing_plan
    FROM public.company_billing cb
    GROUP BY cb.company_external_id
),

demo_attendance AS (
    SELECT
        lower(att.email) AS user_email,
        MIN(d.created_date_utc) AS first_demo_date,
        COUNT(*)                AS demo_count,
        (array_agg(d.organiser_email ORDER BY d.created_date_utc ASC))[1] AS first_demo_organiser_email
    FROM reporting.demo d
    CROSS JOIN LATERAL unnest(d.attendee_emails) WITH ORDINALITY AS att(email, ord)
    WHERE (d.follow_up_context ->> 'is_demo') = 'true'
      AND d.organiser_email ILIKE '%@gomarloo.com'
    GROUP BY lower(att.email)
),

company_demo AS (
    SELECT
        ur.company_external_id,
        bool_or(da.user_email IS NOT NULL)                    AS company_had_demo,
        MIN(da.first_demo_date)                               AS company_first_demo_date,
        (array_agg(da.first_demo_organiser_email
                   ORDER BY da.first_demo_date ASC NULLS LAST)
            FILTER (WHERE da.first_demo_organiser_email IS NOT NULL))[1]
                                                              AS company_first_demo_organiser_email
    FROM public.user_roles ur
    JOIN public.users u2 ON u2.external_id = ur.user_external_id
    LEFT JOIN demo_attendance da ON da.user_email = lower(u2.email)
    WHERE ur.status = 'active'
    GROUP BY ur.company_external_id
)

SELECT
    u.external_id                AS user_external_id,
    ur.company_external_id,
    u.email,
    u.first_name,
    u.last_name,
    TRIM(COALESCE(u.first_name,'') || ' ' || COALESCE(u.last_name,'')) AS full_name,
    u.created_at_utc             AS signup_date_utc,
    ur.role::text                AS user_role,
    cmp.name                     AS company_name,
    cmp.country_code,

    cs.marloo_active_user_count,
    b.on_paid_plan,
    b.billing_plan,
    b.subscription_started_date_utc,
    (b.subscription_started_date_utc IS NOT NULL
        AND b.subscription_started_date_utc > u.created_at_utc) AS converted_after_signup,

    (da.user_email IS NOT NULL) AS had_demo,
    da.first_demo_date,
    da.demo_count,
    da.first_demo_organiser_email,
    cd.company_had_demo,
    cd.company_first_demo_date,
    cd.company_first_demo_organiser_email,

    t.first_transcript_date,
    t.last_transcript_date,
    t.transcript_count,
    s.first_synopsis_date,
    s.last_synopsis_date,
    s.synopsis_count,
    cf.first_calendar_date,
    ch.first_chat_meeting_date,
    ch.chat_meeting_count,
    ch.first_chat_client_date,
    ch.chat_client_count,
    ct.first_custom_template_date,
    ct.custom_template_count,

    EXTRACT(EPOCH FROM (s.first_synopsis_date - u.created_at_utc)) / 86400.0 AS days_to_first_synopsis,
    EXTRACT(EPOCH FROM (now() - u.created_at_utc)) / 86400.0                  AS days_since_signup,
    GREATEST(s.last_synopsis_date, t.last_transcript_date) AS last_active_date,

    u.attribution                 AS attribution_raw,
    u.attribution_category::text  AS attribution_category,
    u.metadata ->> 'utm_source'   AS user_utm_source,
    u.metadata ->> 'utm_medium'   AS user_utm_medium,
    u.metadata ->> 'utm_campaign' AS user_utm_campaign,
    u.metadata ->> 'utm_content'  AS user_utm_content,
    u.metadata ->> 'utm_term'     AS user_utm_term,
    u.fbc                         AS user_fbc,
    u.gclid                       AS user_gclid

FROM public.user_roles ur
JOIN public.users      u   ON u.external_id = ur.user_external_id
JOIN cohort            c   ON u.created_at_utc >= c.start_ts AND u.created_at_utc < c.end_ts

LEFT JOIN public.companies cmp ON cmp.external_id = ur.company_external_id
LEFT JOIN company_size      cs ON cs.company_external_id = ur.company_external_id
LEFT JOIN billing            b ON b.company_external_id = ur.company_external_id
LEFT JOIN transcripts        t ON t.user_external_id  = ur.user_external_id
                              AND t.company_external_id = ur.company_external_id
LEFT JOIN synopses           s ON s.user_external_id  = ur.user_external_id
                              AND s.company_external_id = ur.company_external_id
LEFT JOIN chats             ch ON ch.user_external_id = ur.user_external_id
                              AND ch.company_external_id = ur.company_external_id
LEFT JOIN custom_templates  ct ON ct.user_external_id = ur.user_external_id
                              AND ct.company_external_id = ur.company_external_id
LEFT JOIN calendar_first    cf ON cf.user_external_id = ur.user_external_id
                              AND cf.company_external_id = ur.company_external_id
LEFT JOIN demo_attendance   da ON da.user_email = lower(u.email)
LEFT JOIN company_demo      cd ON cd.company_external_id = ur.company_external_id

WHERE ur.status = 'active'
  AND u.email NOT ILIKE '%@gomarloo.com'

ORDER BY u.created_at_utc DESC, ur.company_external_id
`;

// One row per (user, company) pair. The same user appears multiple times when
// active in multiple workspaces; activity attributes to the workspace it
// happened in. Distinct-user rollups must filter first, then collapse.
export type UserRow = {
  external_id: string;
  user_external_id: string;
  company_external_id: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  created_at_utc: string;
  user_role: string | null;
  company_name: string | null;
  country_code: string | null;
  marloo_active_user_count: number;
  on_paid_plan: boolean;
  billing_plan: string | null;
  subscription_started_date_utc: string | null;
  converted_after_signup: boolean;
  had_demo: boolean;
  first_demo_date: string | null;
  demo_count: number;
  first_demo_organiser_email: string | null;
  company_had_demo: boolean;
  company_first_demo_date: string | null;
  company_first_demo_organiser_email: string | null;
  first_transcript_date: string | null;
  last_transcript_date: string | null;
  transcript_count: number;
  first_synopsis_date: string | null;
  last_synopsis_date: string | null;
  synopsis_count: number;
  first_calendar_date: string | null;
  calendar_connected: boolean;
  first_chat_meeting_date: string | null;
  chat_meeting_count: number;
  first_chat_client_date: string | null;
  chat_client_count: number;
  first_custom_template_date: string | null;
  custom_template_count: number;
  days_to_first_synopsis: number | null;
  days_since_signup: number;
  last_active_date: string | null;
  attribution_raw: string | null;
  attribution_category: string;
  user_utm_source: string | null;
  user_utm_medium: string | null;
  user_utm_campaign: string | null;
  user_utm_content: string | null;
  user_utm_term: string | null;
  user_fbc: string | null;
  user_gclid: string | null;
  logrocket_link: string;
  mixpanel_link: string;
};

function indexer(columns: string[]): Record<string, number> {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  return idx;
}

const num = (v: unknown): number => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const numOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string => (v == null ? "" : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : String(v));
const bool = (v: unknown): boolean => v === true || v === "t" || v === "true";

export function mapRows(columns: string[], rows: unknown[][]): UserRow[] {
  const idx = indexer(columns);
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((row) => {
    const userId = str(get(row, "user_external_id"));
    const firstCalendar = strOrNull(get(row, "first_calendar_date"));
    return {
      external_id: userId,
      user_external_id: userId,
      company_external_id: strOrNull(get(row, "company_external_id")),
      email: str(get(row, "email")),
      first_name: strOrNull(get(row, "first_name")),
      last_name: strOrNull(get(row, "last_name")),
      full_name: str(get(row, "full_name")),
      created_at_utc: str(get(row, "signup_date_utc")),
      user_role: strOrNull(get(row, "user_role")),
      company_name: strOrNull(get(row, "company_name")),
      country_code: strOrNull(get(row, "country_code")),
      marloo_active_user_count: num(get(row, "marloo_active_user_count")),
      on_paid_plan: bool(get(row, "on_paid_plan")),
      billing_plan: strOrNull(get(row, "billing_plan")),
      subscription_started_date_utc: strOrNull(get(row, "subscription_started_date_utc")),
      converted_after_signup: bool(get(row, "converted_after_signup")),
      had_demo: bool(get(row, "had_demo")),
      first_demo_date: strOrNull(get(row, "first_demo_date")),
      demo_count: num(get(row, "demo_count")),
      first_demo_organiser_email: strOrNull(get(row, "first_demo_organiser_email")),
      company_had_demo: bool(get(row, "company_had_demo")),
      company_first_demo_date: strOrNull(get(row, "company_first_demo_date")),
      company_first_demo_organiser_email: strOrNull(get(row, "company_first_demo_organiser_email")),
      first_transcript_date: strOrNull(get(row, "first_transcript_date")),
      last_transcript_date: strOrNull(get(row, "last_transcript_date")),
      transcript_count: num(get(row, "transcript_count")),
      first_synopsis_date: strOrNull(get(row, "first_synopsis_date")),
      last_synopsis_date: strOrNull(get(row, "last_synopsis_date")),
      synopsis_count: num(get(row, "synopsis_count")),
      first_calendar_date: firstCalendar,
      calendar_connected: firstCalendar != null,
      first_chat_meeting_date: strOrNull(get(row, "first_chat_meeting_date")),
      chat_meeting_count: num(get(row, "chat_meeting_count")),
      first_chat_client_date: strOrNull(get(row, "first_chat_client_date")),
      chat_client_count: num(get(row, "chat_client_count")),
      first_custom_template_date: strOrNull(get(row, "first_custom_template_date")),
      custom_template_count: num(get(row, "custom_template_count")),
      days_to_first_synopsis: numOrNull(get(row, "days_to_first_synopsis")),
      days_since_signup: num(get(row, "days_since_signup")),
      last_active_date: strOrNull(get(row, "last_active_date")),
      attribution_raw: strOrNull(get(row, "attribution_raw")),
      attribution_category: str(get(row, "attribution_category")) || "unknown",
      user_utm_source: strOrNull(get(row, "user_utm_source")),
      user_utm_medium: strOrNull(get(row, "user_utm_medium")),
      user_utm_campaign: strOrNull(get(row, "user_utm_campaign")),
      user_utm_content: strOrNull(get(row, "user_utm_content")),
      user_utm_term: strOrNull(get(row, "user_utm_term")),
      user_fbc: strOrNull(get(row, "user_fbc")),
      user_gclid: strOrNull(get(row, "user_gclid")),
      logrocket_link: logrocketLink(userId),
      mixpanel_link: mixpanelLink(userId),
    };
  });
}

export type FunnelStep = {
  key: string;
  label: string;
  predicate: (u: UserRow) => boolean;
  firstDateOf: (u: UserRow) => string | null;
};

export const FUNNEL_STEPS: FunnelStep[] = [
  {
    key: "transcript",
    label: "Got a transcript",
    predicate: (u) => u.transcript_count > 0,
    firstDateOf: (u) => u.first_transcript_date,
  },
  {
    key: "summary",
    label: "Got a summary",
    predicate: (u) => u.synopsis_count > 0,
    firstDateOf: (u) => u.first_synopsis_date,
  },
  {
    key: "calendar",
    label: "Connected calendar",
    predicate: (u) => u.calendar_connected,
    firstDateOf: (u) => u.first_calendar_date,
  },
  {
    key: "ask_marloo_meeting",
    label: "Asked Marloo (meeting)",
    predicate: (u) => u.chat_meeting_count > 0,
    firstDateOf: (u) => u.first_chat_meeting_date,
  },
  {
    key: "ask_marloo_client",
    label: "Asked Marloo (client)",
    predicate: (u) => u.chat_client_count > 0,
    firstDateOf: (u) => u.first_chat_client_date,
  },
  {
    key: "custom_template",
    label: "Custom template",
    predicate: (u) => u.custom_template_count > 0,
    firstDateOf: (u) => u.first_custom_template_date,
  },
  {
    key: "paid",
    label: "Converted to paid",
    predicate: (u) => u.on_paid_plan,
    firstDateOf: (u) => u.subscription_started_date_utc,
  },
];

export const SIGNUP_METRIC: FunnelStep = {
  key: "signup",
  label: "Signed up",
  predicate: () => true,
  firstDateOf: (u) => u.created_at_utc,
};

export const METRIC_OPTIONS: FunnelStep[] = [SIGNUP_METRIC, ...FUNNEL_STEPS];
